import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, paletteTint } from "./types";

// venueListCollage — a "6 ideas" pin with a condensed-caps (Bebas Neue —
// same face as the comparison pin) type treatment. Two styles (A/B):
//   "labeled" (default) — kraft-paper backdrop, header title, and a 2×3 grid
//                         where each tile centers a number over the item name.
//   "title"             — full-bleed 2×3 photo mosaic under a soft WHITE veil,
//                         with one very large BLACK headline filling the pin.
// Footer is the brand wordmark, or a "See all N →" CTA when the title promises
// more than the tiles shown.
//   venue.name / title → title
//   items[]            → up to 6 { name, imageUrl? }
//   collageStyle       → "labeled" | "title"
const WHITE = "#ffffff";
const INK = "#141414";
const HEADER_H = 200;
const FOOTER_H = 84;
// Punchier than the default PHOTO_GRADE (which desaturates + sepias): the 6-tile
// mosaic needs vibrant photos so it doesn't read gray/bland — especially under
// the dark label scrim. Boost saturation, slight contrast, no sepia wash.
const TILE_GRADE = "saturate(1.22) contrast(1.05)";

export function venueListCollageTemplate(input: PinInput): string {
  const { brand, typography, venue, items } = input;
  const style = input.collageStyle === "title" ? "title" : "labeled";
  const title = (venue?.name || input.title || "").trim();
  const list = (items ?? []).filter((i) => i.name).slice(0, 6);
  if (!list.length) throw new Error("venueListCollage requires items[] with names");

  // Backdrop tint (header/footer/gaps): explicit bandTint → palette word in the
  // title → white. So palette-themed "6 Sage Green Ideas" pins pick up the tint.
  const bg = (input.bandTint && input.bandTint.trim()) || paletteTint(title, WHITE);
  const ink = input.textColor || INK; // primary title color (override)
  // "title"-style ink variant: light (DEFAULT) = white fill / thick black
  // outline, lines pulled into a tight centered block; dark = black fill /
  // white outline, lines spread evenly over the mosaic. Pass "dark"
  // explicitly to get the legacy look.
  const lightTitle = input.collageTitleStyle !== "dark";

  const tl = title.length;
  const titleSize = tl <= 16 ? 98 : tl <= 26 ? 82 : tl <= 38 ? 66 : 56;
  // "title" variant — large headline filling the pin (smaller as length grows),
  // additionally capped so a single LONG WORD can't overflow the pin width and
  // clip (condensed caps + 8px tracking over ~900px usable).
  const bigLadder = tl <= 10 ? 340 : tl <= 18 ? 270 : tl <= 28 ? 212 : tl <= 40 ? 168 : tl <= 55 ? 138 : 116;
  const longestWord = title.split(/\s+/).reduce((a, b) => (a.length >= b.length ? a : b), "").length || 1;
  // Condensed-caps advance estimate: ~0.43em per glyph, plus the 8px
  // letter-spacing. Calibrated on Anton; Bebas Neue is slightly NARROWER, so
  // the estimate stays conservative (lines render a touch narrower than
  // predicted — never clipped). Sizes the title so the longest word fits
  // ~900px and wraps our own lines close to how the browser would (~920px).
  const bigSize = Math.max(64, Math.min(bigLadder, Math.floor((900 / longestWord - 8) / 0.43)));
  // Greedy word-wrap into lines WE control, so the big title can be spread with
  // equal vertical spacing (flex space-evenly) instead of a tight centred block.
  const charsPerLine = Math.max(longestWord, Math.floor(920 / (bigSize * 0.43 + 8)));
  const bigLines = (() => {
    const words = title.split(/\s+/).filter(Boolean);
    const out: string[] = [];
    let cur = "";
    for (const w of words) {
      const next = cur ? `${cur} ${w}` : w;
      if (cur && next.length > charsPerLine) { out.push(cur); cur = w; } else cur = next;
    }
    if (cur) out.push(cur);
    return out.slice(0, 6);
  })();

  const leadNum = parseInt((title.match(/^\s*(\d+)/) || [])[1] ?? "", 10);
  const moreCount = Number.isFinite(leadNum) && leadNum > list.length ? leadNum : 0;
  // Footer: an explicit CTA wins (e.g. client pins, so the venue name isn't
  // duplicated in both the title and the footer); else "See all N", else brand.
  const footerText = (input.cta && input.cta.trim())
    ? input.cta.trim()
    : moreCount ? `See all ${moreCount} →` : (brand || "Sauntera Weddings");

  const tiles = list.map((it, i) => {
    const img = it.imageUrl
      ? `<div class="img" style="background-image:url('${escapeHtml(it.imageUrl)}')"></div>`
      : `<div class="img placeholder"></div>`;
    // Numbered label only on the "labeled" style; the "title" style shows the
    // raw photos faintly behind the big headline.
    const cap = style === "labeled"
      ? `<div class="scrim"></div><div class="cap"><div class="num">${i + 1}</div><div class="label">${escapeHtml(it.name)}</div></div>`
      : "";
    return `<div class="tile">${img}${cap}</div>`;
  }).join("");

  // Both variants now sit on a clean white background.
  const footerColor = "#000000";
  const footerBorder = style === "title" ? "transparent" : "rgba(0,0,0,.12)";
  const footerShadow = "none";

  const body = style === "labeled"
    ? `<div class="header"><div class="title">${escapeHtml(title)}</div></div>
       <div class="grid">${tiles}</div>`
    : `<div class="grid plain">${tiles}</div>
       <div class="veil"></div>
       <div class="solo"><div class="bigtitle">${bigLines.map((l) => `<span class="bigline">${escapeHtml(l)}</span>`).join("")}</div></div>`;

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:'Bebas Neue',${typography.bodyFont},sans-serif;-webkit-font-smoothing:antialiased;background:${bg}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${bg}}
  .header{position:absolute;top:0;left:0;right:0;height:${HEADER_H}px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:0 48px;text-align:center}
  .title{font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:${titleSize}px;line-height:1.08;letter-spacing:3px;text-transform:uppercase;
    color:${ink};-webkit-text-stroke:1px ${ink};paint-order:stroke fill;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .grid{position:absolute;display:grid;grid-template-columns:1fr 1fr;grid-template-rows:repeat(3,1fr)}
  .grid:not(.plain){top:${HEADER_H}px;left:0;right:0;bottom:${FOOTER_H}px;gap:10px;padding:0 22px}
  /* No gap in the title variant — the big headline overlays the whole mosaic, so
     flush tiles keep separation lines from cutting through the text. */
  .grid.plain{top:0;left:0;right:0;bottom:${FOOTER_H}px;gap:0;padding:0}
  .tile{position:relative;overflow:hidden;background:#cdb288;border-radius:7px}
  .grid.plain .tile{border-radius:0}
  .img{position:absolute;inset:0;background-size:cover;background-position:center;filter:${TILE_GRADE}}
  .img.placeholder{background:repeating-linear-gradient(45deg,#cdb288,#cdb288 18px,#c2a67b 18px,#c2a67b 36px)}
  .scrim{position:absolute;inset:0;background:rgba(0,0,0,.34)}
  .cap{position:absolute;inset:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:12px;color:#fff;font-family:'Bebas Neue',sans-serif}
  .num{font-size:72px;line-height:.84;letter-spacing:1px;text-shadow:0 2px 12px rgba(0,0,0,.65)}
  .label{font-size:50px;line-height:1.12;letter-spacing:1px;text-transform:uppercase;margin-top:8px;opacity:.9;text-shadow:0 2px 14px rgba(0,0,0,.75);
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-width:96%}
  /* title: soft white veil over the photo mosaic + one big black headline. */
  .veil{position:absolute;top:0;left:0;right:0;bottom:${FOOTER_H}px;z-index:1;background:rgba(255,255,255,.15)}
  .solo{position:absolute;top:0;left:0;right:0;bottom:${FOOTER_H}px;z-index:2;display:flex;align-items:center;justify-content:center;padding:40px 30px}
  /* Dark variant: lines are explicit rows distributed with EQUAL vertical
     spacing (space-evenly), so the inner rows sit evenly between the first and
     last. Light variant: a tight centered block instead — the white-on-photo
     look wants the lines reading as one mass, not spread across the mosaic. */
  /* opacity .9: the title sits slightly transparent so the mosaic reads
     through it faintly instead of the text stamping the photos flat. */
  .bigtitle{font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:${bigSize}px;line-height:1.0;letter-spacing:8px;text-transform:uppercase;text-align:center;opacity:.9;
    color:${lightTitle ? WHITE : ink};-webkit-text-stroke:${lightTitle ? 18 : 12}px ${lightTitle ? INK : WHITE};paint-order:stroke fill;text-shadow:0 3px 16px rgba(0,0,0,.45);
    display:flex;flex-direction:column;align-items:center;justify-content:${lightTitle ? `center;gap:${Math.round(bigSize * 0.22)}px` : "space-evenly"};height:100%;width:100%}
  .bigline{display:block;white-space:nowrap}
  .footer{position:absolute;left:0;right:0;bottom:0;z-index:3;height:${FOOTER_H}px;display:flex;align-items:center;justify-content:center;border-top:1px solid ${footerBorder}}
  .brand{font-family:'Bebas Neue',sans-serif;font-size:36px;letter-spacing:5px;text-transform:uppercase;color:${footerColor};opacity:${style === "title" ? "1" : ".8"};text-shadow:${footerShadow}}
</style></head>
<body><div class="pin">
  ${body}
  <div class="footer"><div class="brand">${escapeHtml(footerText)}</div></div>
</div></body></html>`;
}
